import React, { useCallback, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { AppButton, Box, DeductibleType, FormProvider, Preloader, WarnModal } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { RAW_DATE_FORMAT } from '@/common/constants';
import { ModalWrapper } from '@/components/index.ts';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { useEditDeductibleModalStore } from '@/modules/deductibles/EditDeductibleModal/stores/useEditDeductibleModal.store.ts';
import { AccumulatedItem } from '@/modules/deductibles/SpendTracker/hooks/useDeductibles.ts';
import { Dependent } from '@/modules/employee/employee.types';

import { DeductibleFormFields } from './hooks/useDeductibleForm';
import useDeductibleForm, { DeductibleFormField } from './hooks/useDeductibleForm';
import { useDeductiblePreviewQuery } from './queries/useDeductiblePreview.query';
import { useUpdateDeductibleQuery } from './queries/useUpdateDeductible.query.ts';
import { DeductibleErrorType, formatEffectiveDateError } from './utils/deductibleModalUtils.ts';
import DeductibleForm from './DeductibleForm';
import { TiersModule } from './TiersModule';

const EFFECTIVE_DATE_ERROR = 'Effective date';
const EFFECTIVE_DATE_RANGE_ERROR = 'is not in the valid range';
const WARN_MODAL_HEADER_FUTURE = "Future deductibles can't be updated";
const WARN_MODAL_HEADER_DEFAULT = 'Oops deductible not updated';
const WARN_MODAL_HELPTEXT_DEFAULT = `There was an unexpected error and we couldn't save the data.
  Please check your deductible details and try again. If the issue persists, please reach out to your administrator.`;
const WARN_MODAL_HELPTEXT_FUTURE = `Please check your deductible details and try again. If the issue persists, please reach out to your administrator.`;

interface Props {
  visible: boolean;
  isLoading: boolean;
  familyValue?: number;
  healthPolicyId?: string;
  mode: DeductibleType;
  members?:
    | Dependent[]
    | {
        dependentId: string;
        employeeId: string;
        fullName: string;
        relationshipType: string;
      }[];
  individualAccumulatedValues?: AccumulatedItem[];
  employeeId?: string;
}

export const EditDeductibleModal: React.FC<Props> = ({
  visible,
  isLoading,
  familyValue,
  healthPolicyId,
  mode,
  members,
  individualAccumulatedValues,
  employeeId,
}) => {
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();
  const { methods, defaultValues } = useDeductibleForm(individualAccumulatedValues, familyValue);
  const { handleAddPermanentSnackbar } = useSnackbar();

  const [serverError, setServerError] = useState<string | null>(null);
  const [isErrorModalVisible, setErrorModalVisible] = useState(false);
  const [errorModalHeader, setErrorModalHeader] = useState<string>(WARN_MODAL_HEADER_DEFAULT);
  const [errorModalHelptext, setErrorModalHelptext] = useState<string>(WARN_MODAL_HELPTEXT_DEFAULT);

  const setEditDeductibleModalVisible = useEditDeductibleModalStore((state) => state.setState);

  const { updateDeductibles, isLoading: updateLoading } = useUpdateDeductibleQuery(employeeId || id);
  const { onSubmit, isLoading: isPreviewLoading } = useDeductiblePreviewQuery(employeeId || id);

  const handleOpenEditDeductibleModal = useCallback(
    (val: boolean) => {
      setEditDeductibleModalVisible({ visible: val });
    },
    [setEditDeductibleModalVisible]
  );

  const handleSubmit = useCallback(
    async (formValues: DeductibleFormFields) => {
      if (serverError || !_.isEmpty(methods.formState.errors)) return;

      const effectiveDate = formValues.effectiveDate
        ? dayjs(formValues.effectiveDate).format(RAW_DATE_FORMAT)
        : '';
      const memberDeductible = formValues.memberDeductible || {};

      let individualDeductibles = individualAccumulatedValues?.map((item) => ({
        employee_id: _.toNumber(item.employeeId),
        dependent_id: item.dependentId ? _.toNumber(item.dependentId) : undefined,
        deductibles_amount:
          mode === DeductibleType.Family
            ? _.toNumber(formValues?.familyDeductible)
            : _.toNumber(
                item.dependentId != null
                  ? memberDeductible[item.dependentId]
                  : item.employeeId != null
                    ? memberDeductible[item.employeeId]
                    : undefined
              ),
        effective_date: effectiveDate,
      }));

      if (mode === DeductibleType.Family && individualDeductibles) {
        individualDeductibles = individualDeductibles.filter((item) => !item.dependent_id);
      }

      try {
        await updateDeductibles({
          effective_date: effectiveDate,
          health_policy_id: _.toNumber(healthPolicyId),
          family_deductible_amount: _.toNumber(formValues?.familyDeductible),
          individual_deductibles: individualDeductibles,
        });
        handleAddPermanentSnackbar({
          text: 'Deductibles were updated successfully!',
          closeIcon: true,
          width: '400px',
          state: SnackbarState.positive,
        });
        handleOpenEditDeductibleModal(false);
      } catch (e) {
        const message = _.get(e, 'response.data.elevate_error_message', '') as string;
        if (
          message &&
          message.includes(EFFECTIVE_DATE_ERROR) &&
          message.includes(EFFECTIVE_DATE_RANGE_ERROR)
        ) {
          methods.setError(DeductibleFormField.effectiveDate, {
            type: DeductibleErrorType.Server,
            message: formatEffectiveDateError(message),
          });
          return;
        }
        if (message && message.includes(WARN_MODAL_HEADER_FUTURE)) {
          setErrorModalHeader(WARN_MODAL_HEADER_FUTURE);
          setErrorModalHelptext(WARN_MODAL_HELPTEXT_FUTURE);
        }
        setErrorModalVisible(true);
      }
    },
    [
      serverError,
      methods,
      individualAccumulatedValues,
      mode,
      updateDeductibles,
      healthPolicyId,
      handleAddPermanentSnackbar,
      handleOpenEditDeductibleModal,
    ]
  );

  const handleTryAgain = useCallback(() => {
    setErrorModalVisible(false);
    methods.handleSubmit(handleSubmit)();
  }, [methods, handleSubmit]);

  useEffect(() => {
    if (familyValue || individualAccumulatedValues) {
      methods.reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyValue, individualAccumulatedValues]);

  return (
    <>
      <ModalWrapper
        testId="edit-deductible"
        visible={visible}
        onSetVisible={handleOpenEditDeductibleModal}
        title="Edit Deductibles"
        isClosingByClickOutsideBlocked
      >
        {isLoading ? (
          <Box width="100%" height="100%" align="center" pad={{ top: 'm' }}>
            <Preloader testId="edit-deductible-preloader" />
          </Box>
        ) : (
          <>
            <Box margin={{ top: 'l' }} background="module" pad="l" round="moduleRound">
              <FormProvider {...methods}>
                <DeductibleForm
                  mode={mode}
                  healthPolicyId={healthPolicyId}
                  members={members}
                  familyValue={familyValue}
                  individualAccumulatedValues={individualAccumulatedValues}
                  employeeId={employeeId || id}
                  serverError={serverError}
                  setServerError={setServerError}
                  onSubmit={onSubmit}
                />
              </FormProvider>
              <Box height="1px" background="border1" margin={{ vertical: 'm' }} />
              <TiersModule />
            </Box>
            <Box direction="row" justify="end" margin={{ top: 'spacing24' }}>
              <Box margin={{ right: 's' }}>
                <AppButton
                  width="140px"
                  buttonType="secondary"
                  testId="AddEnrollmentModal-close-modal"
                  onClick={() => handleOpenEditDeductibleModal(false)}
                >
                  Cancel
                </AppButton>
              </Box>
              <AppButton
                testId="edit-deductible-submit"
                width="133px"
                onClick={methods.handleSubmit(handleSubmit)}
                disabled={updateLoading || isPreviewLoading}
              >
                {updateLoading ? <Preloader color="white" /> : 'Submit'}
              </AppButton>
            </Box>
          </>
        )}
      </ModalWrapper>
      <WarnModal
        testId="edit-deductible-error-modal"
        visible={isErrorModalVisible}
        onSetVisible={() => setErrorModalVisible(false)}
        header={errorModalHeader}
        helptext={errorModalHelptext}
        buttonText="Cancel"
        submitButtonNode={
          <AppButton testId="update_deductible_modal_submit" width="control" onClick={handleTryAgain}>
            Try again
          </AppButton>
        }
        paddingless
      />
    </>
  );
};
